import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  UserPlus, Loader2, Search, Pencil,
  Ban, RefreshCw, LogOut, CheckCircle2, AlertCircle, X,
  FileText, ShieldAlert, Eye, GitCompare, Monitor, Trash2,
} from "lucide-react";
import RoleBadge from "@/components/RoleBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  tenant_admin: "Tenant Admin",
  hr_admin: "HR Admin",
  hrbp: "HR Business Partner",
  manager: "Manager",
  employee: "Employee",
};
const ROLE_OPTIONS = ["super_admin", "tenant_admin", "hr_admin", "hrbp", "manager", "employee"];

const USER_TYPE_TONE = {
  admin: "bg-rose-50 text-rose-700 border-rose-200",
  people_leader: "bg-violet-50 text-violet-700 border-violet-200",
  individual_contributor: "bg-slate-100 text-slate-600 border-slate-200",
};
const USER_TYPE_LABEL = {
  admin: "Admin",
  people_leader: "People Leader",
  individual_contributor: "IC",
};

const ROLE_BLURB = {
  super_admin: "Cross-tenant operator. Can switch tenant context and manage everything.",
  tenant_admin: "Full control over this tenant: settings, billing, users, cycles.",
  hr_admin: "Owns the HR program: cycles, frameworks, calibration, employee data.",
  hrbp: "Scoped HR partner for specific business units.",
  manager: "Leads direct reports — approves goals, runs reviews, keeps a coaching log.",
  employee: "Standard team member — set goals, write self-reviews, log wins.",
};

import AdminImport from "@/pages/AdminImport";

export default function AdminUsers() {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = location.pathname.endsWith("/permissions")
    ? "permissions"
    : location.pathname.endsWith("/invites")
      ? "invites"
      : location.pathname.endsWith("/import")
        ? "import"
        : "people";

  return (
    <div className="max-w-7xl space-y-6" data-testid="admin-users-page">
      <header>
        <div className="axia-overline">Admin · Users</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">
          Users & Roles
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">
          Invite people, manage role assignments, bulk-import from a CSV, and review the permission model.
        </p>
      </header>

      <nav className="flex items-center gap-1 border-b border-slate-200">
        <TabBtn active={tab === "people"} onClick={() => navigate("/admin/users")} testid="tab-people">
          People
        </TabBtn>
        <TabBtn active={tab === "invites"} onClick={() => navigate("/admin/users/invites")} testid="tab-invites">
          Invites
        </TabBtn>
        <TabBtn active={tab === "import"} onClick={() => navigate("/admin/users/import")} testid="tab-import">
          Import
        </TabBtn>
        <TabBtn active={tab === "permissions"} onClick={() => navigate("/admin/users/permissions")} testid="tab-permissions">
          Permissions
        </TabBtn>
      </nav>

      {tab === "people" && <PeopleTab />}
      {tab === "invites" && <InvitesTab />}
      {tab === "import" && <AdminImport embedded />}
      {tab === "permissions" && <PermissionsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition",
        active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900",
      )}
    >
      {children}
    </button>
  );
}

/* ====================================================================== */
/* People Tab                                                              */
/* ====================================================================== */
function PeopleTab() {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fRole, setFRole] = useState("all");
  const [fType, setFType] = useState("all");
  const [fDept, setFDept] = useState("all");
  const [openInvite, setOpenInvite] = useState(false);
  const [activeUserId, setActiveUserId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (fStatus !== "all") p.user_status = fStatus;
    if (fRole !== "all") p.role = fRole;
    if (fType !== "all") p.user_type = fType;
    if (fDept !== "all") p.department = fDept;
    return p;
  }, [q, fStatus, fRole, fType, fDept]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/users", { params }),
      api.get("/employees"),
      api.get("/users/stats"),
    ])
      .then(([u, e, s]) => {
        setUsers(u.data.items || []);
        setEmployees(e.data.items || []);
        setStats(s.data || null);
      })
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(load, [load]);

  // Reset selection when filters change
  useEffect(() => { setSelected(new Set()); }, [q, fStatus, fRole, fType, fDept]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === users.length) return new Set();
      return new Set(users.map((u) => u.id));
    });
  };

  const bulkAction = async (action, opts = {}) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const body = { user_ids: ids, action };
    if (opts.roles) body.roles = opts.roles;
    try {
      const r = await api.post("/users/bulk", body);
      const n = r.data.succeeded?.length || 0;
      const s = r.data.skipped?.length || 0;
      if (n > 0 && s === 0) toast.success(`Updated ${n} user${n === 1 ? "" : "s"}`);
      else if (n > 0) toast.success(`Updated ${n} · ${s} skipped`);
      else toast.error(`Nothing applied — ${s} skipped`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    }
  };

  return (
    <>
      {stats && <StatsStrip stats={stats} />}

      <FilterBar
        q={q} setQ={setQ}
        fStatus={fStatus} setFStatus={setFStatus}
        fRole={fRole} setFRole={setFRole}
        fType={fType} setFType={setFType}
        fDept={fDept} setFDept={setFDept}
        departments={stats?.departments || []}
        onInvite={() => setOpenInvite(true)}
        onClear={() => { setQ(""); setFStatus("all"); setFRole("all"); setFType("all"); setFDept("all"); }}
      />

      <Dialog open={openInvite} onOpenChange={setOpenInvite}>
        <InviteDialog employees={employees} onClose={() => { setOpenInvite(false); load(); }} />
      </Dialog>

      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onAction={bulkAction}
        />
      )}

      <UsersTable
        users={users}
        loading={loading}
        onRowClick={setActiveUserId}
        selected={selected}
        onToggleOne={toggleOne}
        onToggleAll={toggleAll}
      />

      <UserDetailDrawer
        userId={activeUserId}
        onClose={() => setActiveUserId(null)}
        onMutated={load}
        employees={employees}
      />
    </>
  );
}

function StatsStrip({ stats }) {
  const items = [
    { label: "Total accounts", value: stats.total, tone: "text-slate-900" },
    { label: "Active", value: stats.by_status?.active || 0, tone: "text-emerald-700" },
    { label: "Invited", value: stats.by_status?.invited || 0, tone: "text-blue-700" },
    { label: "Suspended", value: stats.by_status?.suspended || 0, tone: "text-amber-700" },
    { label: "Admins", value: stats.by_type?.admin || 0, tone: "text-rose-700" },
    { label: "People leaders", value: stats.by_type?.people_leader || 0, tone: "text-violet-700" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="users-stats-strip">
      {items.map((it) => (
        <div key={it.label} className="axia-card px-4 py-3" data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{it.label}</div>
          <div className={cn("font-display text-2xl font-semibold mt-0.5", it.tone)}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function FilterBar({
  q, setQ, fStatus, setFStatus, fRole, setFRole, fType, setFType,
  fDept, setFDept, departments, onInvite, onClear,
}) {
  const hasActive =
    q || fStatus !== "all" || fRole !== "all" || fType !== "all" || fDept !== "all";
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 bg-slate-50 border-slate-200 w-[220px]"
            data-testid="users-search"
          />
        </div>
        <FilterSelect value={fStatus} onChange={setFStatus} placeholder="Status"
          testid="filter-status" options={[
            ["all", "Any status"], ["active", "Active"], ["invited", "Invited"], ["suspended", "Suspended"],
          ]} />
        <FilterSelect value={fRole} onChange={setFRole} placeholder="Role"
          testid="filter-role" options={[
            ["all", "Any role"],
            ["super_admin", "Super Admin"], ["tenant_admin", "Tenant Admin"], ["hr_admin", "HR Admin"],
            ["hrbp", "HRBP"], ["manager", "Manager"], ["employee", "Employee"],
          ]} />
        <FilterSelect value={fType} onChange={setFType} placeholder="Type"
          testid="filter-type" options={[
            ["all", "Any type"], ["admin", "Admin"],
            ["people_leader", "People Leader"], ["individual_contributor", "IC"],
          ]} />
        {departments.length > 0 && (
          <FilterSelect value={fDept} onChange={setFDept} placeholder="Department"
            testid="filter-department" options={[
              ["all", "Any department"],
              ...departments.map((d) => [d, d]),
            ]} />
        )}
        {hasActive && (
          <button onClick={onClear} className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="filters-clear">
            Clear filters
          </button>
        )}
      </div>
      <Button onClick={onInvite} data-testid="invite-user-btn">
        <UserPlus className="w-4 h-4 mr-2" /> Invite user
      </Button>
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[150px]" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function BulkActionBar({ count, onClear, onAction }) {
  const [confirmAction, setConfirmAction] = useState(null);
  const apply = (action) => {
    if (action === "suspend") setConfirmAction({ action, label: `Suspend ${count} user${count === 1 ? "" : "s"}? All sessions will be revoked.` });
    else if (action === "reactivate") onAction("reactivate");
    else if (action === "revoke-sessions") setConfirmAction({ action, label: `Sign out ${count} user${count === 1 ? "" : "s"} from all devices?` });
  };
  return (
    <>
      <div className="axia-card px-4 py-2.5 flex items-center justify-between gap-3 sticky top-2 z-10 bg-slate-900 text-white" data-testid="bulk-action-bar">
        <div className="text-sm">
          <span className="font-semibold">{count}</span> selected
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => apply("reactivate")} className="text-emerald-300 hover:bg-slate-800 hover:text-emerald-200" data-testid="bulk-reactivate-btn">
            <CheckCircle2 className="w-3 h-3 mr-1.5" /> Reactivate
          </Button>
          <Button size="sm" variant="ghost" onClick={() => apply("suspend")} className="text-amber-300 hover:bg-slate-800 hover:text-amber-200" data-testid="bulk-suspend-btn">
            <Ban className="w-3 h-3 mr-1.5" /> Suspend
          </Button>
          <Button size="sm" variant="ghost" onClick={() => apply("revoke-sessions")} className="text-slate-200 hover:bg-slate-800" data-testid="bulk-revoke-btn">
            <LogOut className="w-3 h-3 mr-1.5" /> Sign out
          </Button>
          <button onClick={onClear} className="text-slate-300 hover:text-white p-1.5" data-testid="bulk-clear-btn">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm bulk action</DialogTitle>
            <DialogDescription>{confirmAction?.label}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button onClick={() => { onAction(confirmAction.action); setConfirmAction(null); }} data-testid="bulk-confirm-btn">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UsersTable({ users, loading, onRowClick, selected, onToggleOne, onToggleAll }) {
  const allSelected = users.length > 0 && selected.size === users.length;
  return (
    <div className="axia-card overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="w-10">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                aria-label="Select all"
                data-testid="select-all-checkbox"
              />
            </TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Name</TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Email</TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Type</TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Designation</TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Department</TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Roles</TableHead>
            <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={9} className="text-sm text-slate-500 text-center py-8">Loading…</TableCell></TableRow>
          ) : users.length === 0 ? (
            <TableRow><TableCell colSpan={9} className="text-sm text-slate-500 text-center py-8">No users found.</TableCell></TableRow>
          ) : (
            users.map((u) => {
              const checked = selected.has(u.id);
              return (
                <TableRow
                  key={u.id}
                  className={cn("hover:bg-slate-50/60 transition", checked && "bg-slate-50")}
                  data-testid={`user-row-${u.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()} className="w-10">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleOne(u.id)}
                      aria-label={`Select ${u.email}`}
                      data-testid={`select-row-${u.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-slate-900 cursor-pointer" onClick={() => onRowClick(u.id)}>{u.display_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-700 cursor-pointer" onClick={() => onRowClick(u.id)}>{u.email}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => onRowClick(u.id)}>
                    <span className={cn("inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium", USER_TYPE_TONE[u.user_type] || USER_TYPE_TONE.individual_contributor)} data-testid={`type-pill-${u.id}`}>
                      {USER_TYPE_LABEL[u.user_type] || "IC"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 cursor-pointer" onClick={() => onRowClick(u.id)}>{u.designation || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600 cursor-pointer" onClick={() => onRowClick(u.id)}>{u.department || "—"}</TableCell>
                  <TableCell className="cursor-pointer" onClick={() => onRowClick(u.id)}>
                    <div className="flex gap-1 flex-wrap">
                      {(u.roles || []).map((r) => <RoleBadge key={r} role={ROLE_LABEL[r] || r} />)}
                    </div>
                  </TableCell>
                  <TableCell className="cursor-pointer" onClick={() => onRowClick(u.id)}>
                    <StatusPill status={u.status} />
                  </TableCell>
                  <TableCell className="text-right cursor-pointer" onClick={() => onRowClick(u.id)}>
                    <Pencil className="w-3.5 h-3.5 text-slate-300" />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "invited" ? "bg-blue-50 text-blue-700 border-blue-200"
        : status === "suspended" ? "bg-amber-50 text-amber-700 border-amber-200"
          : "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={cn("axia-pill", cls)}>{status}</span>;
}

/* ====================================================================== */
/* Invite Dialog                                                            */
/* ====================================================================== */
function InviteDialog({ employees, onClose }) {
  const [email, setEmail] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [role, setRole] = useState("employee");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/users/invite", {
        email: email.trim().toLowerCase(),
        employee_id: employeeId || null,
        roles: [role],
      });
      toast.success("Invitation sent");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent data-testid="invite-dialog">
      <DialogHeader>
        <DialogTitle>Invite a user</DialogTitle>
        <DialogDescription>They'll receive a magic link valid for 48 hours. No password required.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label htmlFor="invite-email">Email</Label>
          <Input id="invite-email" data-testid="invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" />
        </div>
        <div>
          <Label>Link to employee record (optional)</Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger data-testid="invite-employee-select"><SelectValue placeholder="No employee record" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.designation}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="hrbp">HR Business Partner</SelectItem>
              <SelectItem value="hr_admin">HR Admin</SelectItem>
              <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={loading} data-testid="invite-submit">
          {loading && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Send invitation
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ====================================================================== */
/* Invites Tab                                                              */
/* ====================================================================== */
function InvitesTab() {
  const [invited, setInvited] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pasted, setPasted] = useState("");
  const [role, setRole] = useState("employee");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [activeUserId, setActiveUserId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/users", { params: { user_status: "invited" } })
      .then((r) => setInvited(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const parsedEmails = useMemo(() => {
    return Array.from(new Set(
      pasted
        .split(/[\s,;\n]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && s.includes("@")),
    ));
  }, [pasted]);

  const send = async () => {
    if (parsedEmails.length === 0) {
      toast.error("Paste at least one valid email");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/users/invite/bulk", { emails: parsedEmails, roles: [role] });
      const sent = r.data.sent?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      if (sent > 0) toast.success(`${sent} invite${sent === 1 ? "" : "s"} sent · ${skipped} skipped`);
      else toast.error(`No invites sent · ${skipped} skipped`);
      setResult(r.data);
      setPasted("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally {
      setSending(false);
    }
  };

  const resend = async (uid) => {
    try {
      const r = await api.post(`/users/${uid}/resend-invite`);
      toast.success("Invite resent");
      if (r.data?.invite_link) {
        try { await navigator.clipboard.writeText(r.data.invite_link); toast.info("Invite link copied"); } catch {}
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const revoke = async (uid) => {
    if (!confirm("Revoke this invite? The pending account will be removed.")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="invites-tab">
      <section className="axia-card p-5 space-y-4">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Send invites in bulk</h3>
          <p className="text-xs text-slate-500 mt-1">Paste emails separated by spaces, commas, semicolons, or new lines. Each invitee gets a magic link valid for 48 hours.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="bulk-emails">Emails</Label>
            <textarea
              id="bulk-emails"
              data-testid="bulk-invite-emails"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="alice@company.com, bob@company.com&#10;carol@company.com"
              rows={5}
              className="mt-1.5 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-mono focus:outline-none focus:border-slate-400"
            />
            <div className="text-[11px] text-slate-500 mt-1.5">
              {parsedEmails.length === 0
                ? (pasted.trim() ? "No valid emails detected." : "Paste some emails to begin.")
                : `${parsedEmails.length} unique email${parsedEmails.length === 1 ? "" : "s"} ready to send.`}
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="hrbp">HR Business Partner</SelectItem>
                  <SelectItem value="hr_admin">HR Admin</SelectItem>
                  <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={send}
              disabled={sending || parsedEmails.length === 0}
              className="w-full"
              data-testid="bulk-invite-submit"
            >
              {sending && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
              Send {parsedEmails.length > 0 ? `${parsedEmails.length} ` : ""}invite{parsedEmails.length === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
        {result && (
          <BulkInviteResult result={result} />
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-semibold text-slate-900">Pending invites</h3>
          <span className="text-xs text-slate-500">{invited.length} pending</span>
        </div>
        <div className="axia-card overflow-hidden">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Email</TableHead>
                <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Role</TableHead>
                <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Linked employee</TableHead>
                <TableHead className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Invited</TableHead>
                <TableHead className="text-right text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-sm text-slate-500 text-center py-8">Loading…</TableCell></TableRow>
              ) : invited.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-sm text-slate-500 text-center py-8">No pending invites.</TableCell></TableRow>
              ) : (
                invited.map((u) => (
                  <TableRow key={u.id} data-testid={`invite-row-${u.id}`}>
                    <TableCell className="font-mono text-xs text-slate-700">{u.email}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {(u.roles || []).map((r) => <RoleBadge key={r} role={ROLE_LABEL[r] || r} />)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {u.display_name || <span className="text-slate-400 italic">— unlinked —</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setActiveUserId(u.id)} className="h-7 px-2" data-testid={`invite-detail-${u.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => resend(u.id)} className="h-7 px-2 text-blue-700" data-testid={`invite-resend-${u.id}`}>
                          <RefreshCw className="w-3 h-3 mr-1" /> Resend
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => revoke(u.id)} className="h-7 px-2 text-rose-700" data-testid={`invite-revoke-${u.id}`}>
                          <X className="w-3 h-3 mr-1" /> Revoke
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <UserDetailDrawer
        userId={activeUserId}
        onClose={() => setActiveUserId(null)}
        onMutated={load}
        employees={[]}
      />
    </div>
  );
}

function BulkInviteResult({ result }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="bulk-invite-result">
      <div className="text-xs font-medium text-slate-700 mb-1.5">
        Result: {result.sent?.length || 0} sent · {result.skipped?.length || 0} skipped
      </div>
      {(result.skipped || []).length > 0 && (
        <ul className="text-[11px] text-amber-700 space-y-0.5">
          {result.skipped.map((s, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <AlertCircle className="w-2.5 h-2.5 shrink-0 mt-0.5" />
              <span className="font-mono">{s.email}</span>
              <span className="text-slate-500">— {s.reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



/* ====================================================================== */
/* User Detail Drawer                                                       */
/* ====================================================================== */
function UserDetailDrawer({ userId, onClose, onMutated, employees }) {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("profile");
  const [savingRoles, setSavingRoles] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [linkingEmp, setLinkingEmp] = useState("");
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityCategory, setActivityCategory] = useState("all");
  const [activityCursor, setActivityCursor] = useState(null);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await api.get(`/users/${userId}`);
      setData(r.data);
      setEmailValue(r.data.email || "");
      setLinkingEmp(r.data.employee_id || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    if (userId) reload();
    setTab("profile"); setEditEmail(false);
    setActivity([]); setActivityCursor(null); setActivityHasMore(false); setActivityCategory("all");
    setSessions([]);
  }, [userId, reload]);

  const loadActivity = async ({ append = false, category = activityCategory } = {}) => {
    if (!userId) return;
    setActivityLoading(true);
    try {
      const params = { limit: 50 };
      if (category && category !== "all") params.category = category;
      if (append && activityCursor) params.before = activityCursor;
      const r = await api.get(`/users/${userId}/activity`, { params });
      const items = r.data.items || [];
      setActivity((prev) => (append ? [...prev, ...items] : items));
      setActivityCursor(r.data.next_cursor || null);
      setActivityHasMore(!!r.data.has_more);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity");
    } finally {
      setActivityLoading(false);
    }
  };
  useEffect(() => {
    if (tab === "activity") loadActivity({ append: false, category: activityCategory });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tab, userId, activityCategory]);

  const loadSessions = async () => {
    if (!userId) return;
    setSessionsLoading(true);
    try {
      const r = await api.get(`/users/${userId}/sessions`);
      setSessions(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sessions");
    } finally {
      setSessionsLoading(false);
    }
  };
  useEffect(() => { if (tab === "sessions") loadSessions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, userId]);

  const revokeOneSession = async (sessionId) => {
    if (!confirm("Revoke this session? The user will be signed out on that device.")) return;
    try {
      await api.delete(`/users/${data.id}/sessions/${sessionId}`);
      toast.success("Session revoked");
      loadSessions();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!userId) return null;

  const isMe = data && me?.id === data.id;
  const myRoles = me?.roles || [];
  const isSuper = myRoles.includes("super_admin");
  const isTenantAdmin = myRoles.includes("tenant_admin");
  const isHrAdmin = myRoles.includes("hr_admin");
  const targetIsAdmin = data ? (data.roles || []).some((r) => r === "super_admin" || r === "tenant_admin") : false;
  const canEdit = !!data && (isSuper || isTenantAdmin || (isHrAdmin && !targetIsAdmin));

  const toggleRole = async (role) => {
    if (!canEdit) return;
    const current = new Set(data.roles || []);
    if (current.has(role)) current.delete(role); else current.add(role);
    if (current.size === 0) {
      toast.error("A user must have at least one role");
      return;
    }
    setSavingRoles(true);
    try {
      const r = await api.patch(`/users/${data.id}`, { roles: Array.from(current) });
      setData(r.data);
      toast.success("Roles updated");
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update roles");
    } finally {
      setSavingRoles(false);
    }
  };

  const saveEmail = async () => {
    if (!emailValue.includes("@")) return toast.error("Invalid email");
    setSavingEmail(true);
    try {
      const r = await api.patch(`/users/${data.id}`, { email: emailValue.toLowerCase().trim() });
      setData(r.data);
      setEditEmail(false);
      toast.success("Email updated");
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  };

  const saveEmployeeLink = async () => {
    try {
      const r = await api.patch(`/users/${data.id}`, { employee_id: linkingEmp || "" });
      setData(r.data);
      toast.success(linkingEmp ? "Employee linked" : "Employee unlinked");
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const suspend = async () => {
    if (!confirm("Suspend this user? All their sessions will be revoked.")) return;
    try {
      await api.post(`/users/${data.id}/suspend`);
      toast.success("User suspended");
      reload(); onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const reactivate = async () => {
    try {
      await api.post(`/users/${data.id}/reactivate`);
      toast.success("User reactivated");
      reload(); onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const revokeSessions = async () => {
    if (!confirm("Force sign-out from all devices?")) return;
    try {
      const r = await api.post(`/users/${data.id}/revoke-sessions`);
      toast.success(`Signed out ${r.data.sessions_revoked || 0} session${r.data.sessions_revoked === 1 ? "" : "s"}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const resendInvite = async () => {
    try {
      const r = await api.post(`/users/${data.id}/resend-invite`);
      toast.success("Invite resent");
      if (r.data?.invite_link) {
        try { await navigator.clipboard.writeText(r.data.invite_link); toast.info("Invite link copied"); } catch {}
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 transition-opacity",
        userId ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
      )}
      data-testid="user-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl border-l border-slate-200 overflow-y-auto"
      >
        {loading || !data ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : (
          <>
            <header className="sticky top-0 bg-white border-b border-slate-200 z-10">
              <div className="flex items-start justify-between p-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                    {((data.display_name || data.email)?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg font-semibold text-slate-900 truncate">
                      {data.display_name || "Unlinked account"}
                    </div>
                    <div className="font-mono text-xs text-slate-500 break-all">{data.email}</div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className={cn("inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium", USER_TYPE_TONE[data.user_type])} data-testid="drawer-type-pill">
                        {USER_TYPE_LABEL[data.user_type] || "IC"}
                      </span>
                      <StatusPill status={data.status} />
                      {(data.roles || []).map((r) => <RoleBadge key={r} role={ROLE_LABEL[r] || r} />)}
                    </div>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1.5" data-testid="drawer-close-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {canEdit && (
                <div className="flex items-center gap-2 px-5 pb-4 flex-wrap">
                  {data.status === "invited" && (
                    <Button variant="outline" size="sm" onClick={resendInvite} data-testid="resend-invite-btn">
                      <RefreshCw className="w-3 h-3 mr-1.5" /> Resend invite
                    </Button>
                  )}
                  {data.status !== "suspended" ? (
                    !isMe && (
                      <Button variant="outline" size="sm" onClick={suspend} data-testid="suspend-btn" className="text-amber-700 border-amber-200 hover:bg-amber-50">
                        <Ban className="w-3 h-3 mr-1.5" /> Suspend
                      </Button>
                    )
                  ) : (
                    <Button variant="outline" size="sm" onClick={reactivate} data-testid="reactivate-btn" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                      <CheckCircle2 className="w-3 h-3 mr-1.5" /> Reactivate
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={revokeSessions} data-testid="revoke-sessions-btn">
                    <LogOut className="w-3 h-3 mr-1.5" /> Force sign-out
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setViewAsOpen(true)} data-testid="view-as-btn">
                    <Eye className="w-3 h-3 mr-1.5" /> View as
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-1 px-5 -mb-px border-b border-slate-200">
                <DrawerTab active={tab === "profile"} onClick={() => setTab("profile")} testid="drawer-tab-profile">Profile</DrawerTab>
                <DrawerTab active={tab === "roles"} onClick={() => setTab("roles")} testid="drawer-tab-roles">Roles & access</DrawerTab>
                <DrawerTab active={tab === "sessions"} onClick={() => setTab("sessions")} testid="drawer-tab-sessions">Sessions</DrawerTab>
                <DrawerTab active={tab === "activity"} onClick={() => setTab("activity")} testid="drawer-tab-activity">Activity</DrawerTab>
              </div>
            </header>

            <div className="p-5 space-y-5">
              {tab === "profile" && (
                <ProfilePane
                  data={data}
                  canEdit={canEdit}
                  editEmail={editEmail}
                  setEditEmail={setEditEmail}
                  emailValue={emailValue}
                  setEmailValue={setEmailValue}
                  saveEmail={saveEmail}
                  savingEmail={savingEmail}
                  linkingEmp={linkingEmp}
                  setLinkingEmp={setLinkingEmp}
                  saveEmployeeLink={saveEmployeeLink}
                  employees={employees}
                />
              )}
              {tab === "roles" && (
                <RolesPane
                  data={data}
                  toggleRole={toggleRole}
                  savingRoles={savingRoles}
                  canEdit={canEdit}
                  isSuper={isSuper}
                  isTenantAdmin={isTenantAdmin}
                  isHrAdmin={isHrAdmin}
                  isMe={isMe}
                />
              )}
              {tab === "sessions" && (
                <SessionsPane items={sessions} loading={sessionsLoading} onRevoke={revokeOneSession} canEdit={canEdit} isSelf={isMe} />
              )}
              {tab === "activity" && (
                <ActivityPane
                  items={activity}
                  loading={activityLoading}
                  category={activityCategory}
                  setCategory={setActivityCategory}
                  hasMore={activityHasMore}
                  onLoadMore={() => loadActivity({ append: true })}
                />
              )}
            </div>
          </>
        )}
      </aside>
      {viewAsOpen && data && (
        <ViewAsDialog data={data} onClose={() => setViewAsOpen(false)} />
      )}
    </div>
  );
}

function DrawerTab({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "px-3 py-2.5 text-sm font-medium border-b-2 transition",
        active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900",
      )}
    >{children}</button>
  );
}

function ProfilePane({
  data, canEdit, editEmail, setEditEmail, emailValue, setEmailValue,
  saveEmail, savingEmail, linkingEmp, setLinkingEmp, saveEmployeeLink, employees,
}) {
  const emp = data.employee;
  const linkedChanged = (linkingEmp || "") !== (data.employee_id || "");
  return (
    <>
      <section className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
          {canEdit && !editEmail && (
            <button onClick={() => setEditEmail(true)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center" data-testid="edit-email-btn">
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </button>
          )}
        </div>
        {editEmail ? (
          <div className="flex items-center gap-2">
            <Input value={emailValue} onChange={(e) => setEmailValue(e.target.value)} className="font-mono text-sm" data-testid="email-edit-input" />
            <Button size="sm" onClick={saveEmail} disabled={savingEmail} data-testid="save-email-btn">
              {savingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditEmail(false); setEmailValue(data.email); }}>Cancel</Button>
          </div>
        ) : (
          <div className="font-mono text-sm text-slate-800">{data.email}</div>
        )}
        {editEmail && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded p-2 mt-2 flex items-start gap-1.5">
            <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
            Email changes take effect immediately. The user will sign in with the new address from their next OTP request.
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Linked employee record</Label>
        </div>
        {emp ? (
          <div className="space-y-1 text-sm">
            <div className="font-medium text-slate-900">{emp.first_name} {emp.last_name}</div>
            <div className="text-slate-600">{emp.designation || "—"} · {emp.department || "—"}</div>
            <div className="text-xs text-slate-500">Employee ID: <span className="font-mono">{emp.employee_id || emp.id}</span></div>
          </div>
        ) : (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded p-2.5 flex items-start gap-1.5 mb-2">
            <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
            This account isn't linked to an employee record. They can sign in and admin the workspace, but won't appear in performance cycles.
          </div>
        )}
        {canEdit && (
          <div className="mt-3 flex items-center gap-2">
            <Select value={linkingEmp || "none"} onValueChange={(v) => setLinkingEmp(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="link-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Unlinked —</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.designation || ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={saveEmployeeLink} disabled={!linkedChanged} data-testid="save-link-btn">Apply</Button>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 p-4 grid grid-cols-2 gap-4 text-sm">
        <Meta label="Active sessions" value={data.session_count ?? 0} />
        <Meta label="Last login" value={data.last_login_at ? new Date(data.last_login_at).toLocaleString() : "—"} />
        <Meta label="Account created" value={data.created_at ? new Date(data.created_at).toLocaleDateString() : "—"} />
        <Meta label="Status" value={data.status} />
      </section>
    </>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{String(value)}</div>
    </div>
  );
}

function RolesPane({ data, toggleRole, savingRoles, canEdit, isSuper, isTenantAdmin, isHrAdmin, isMe }) {
  const current = new Set(data.roles || []);
  const canAssign = (role) => {
    if (!canEdit) return false;
    if (role === "super_admin") return isSuper;
    if (role === "tenant_admin") return isSuper || isTenantAdmin;
    if (role === "hr_admin") return isSuper || isTenantAdmin;
    return isSuper || isTenantAdmin || isHrAdmin;
  };
  return (
    <>
      <section className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Assigned roles</Label>
          {savingRoles && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </div>
        <div className="grid grid-cols-1 gap-2">
          {ROLE_OPTIONS.map((r) => {
            const checked = current.has(r);
            const allowed = canAssign(r);
            return (
              <label
                key={r}
                className={cn(
                  "flex items-start gap-3 px-3 py-2.5 rounded border transition",
                  checked ? "bg-slate-50 border-slate-300" : "bg-white border-slate-200",
                  allowed ? "cursor-pointer hover:border-slate-300" : "opacity-50 cursor-not-allowed",
                )}
                data-testid={`role-toggle-${r}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={checked}
                  disabled={!allowed || savingRoles}
                  onChange={() => toggleRole(r)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <RoleBadge role={ROLE_LABEL[r]} />
                    <span className="text-sm font-medium text-slate-800">{ROLE_LABEL[r]}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{ROLE_BLURB[r]}</div>
                </div>
              </label>
            );
          })}
        </div>
        {isMe && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded p-2 mt-3 flex items-start gap-1.5">
            <ShieldAlert className="w-3 h-3 shrink-0 mt-0.5" />
            You can't remove your own Super Admin or Tenant Admin role — ask another admin to do it.
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Effective permissions</Label>
          <Link to="/admin/users/permissions" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center" data-testid="open-permissions-link">
            Reference <FileText className="w-3 h-3 ml-1" />
          </Link>
        </div>
        <p className="text-xs text-slate-500 mb-3">{(data.effective_capabilities || []).length} capabilities granted via the assigned roles.</p>
        <EffectiveCapsList items={data.effective_capabilities || []} />
      </section>
    </>
  );
}

function EffectiveCapsList({ items }) {
  const grouped = useMemo(() => {
    const out = {};
    items.forEach((c) => { (out[c.module_label] = out[c.module_label] || []).push(c); });
    return out;
  }, [items]);
  if (!items.length) {
    return <div className="text-xs text-slate-400 italic">No granted capabilities.</div>;
  }
  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([mod, caps]) => (
        <div key={mod}>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{mod}</div>
          <ul className="space-y-1">
            {caps.map((c) => (
              <li key={c.key} className="flex items-start gap-2 text-xs text-slate-700">
                <LevelDot level={c.level} />
                <div className="flex-1">
                  <span className="font-medium">{c.label}</span>
                  {c.level !== "full" && (
                    <span className={cn("ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium", LEVEL_TONE[c.level])}>{c.level}</span>
                  )}
                  {c.rationale && <div className="text-slate-500 italic">{c.rationale}</div>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const LEVEL_TONE = {
  full: "bg-emerald-50 text-emerald-700",
  scoped: "bg-blue-50 text-blue-700",
  conditional: "bg-amber-50 text-amber-700",
  no: "bg-slate-100 text-slate-500",
};
function LevelDot({ level }) {
  const c = level === "full" ? "bg-emerald-500"
    : level === "scoped" ? "bg-blue-500"
      : level === "conditional" ? "bg-amber-500"
        : "bg-slate-300";
  return <span className={cn("w-1.5 h-1.5 rounded-full mt-1.5 shrink-0", c)} />;
}

const ACTIVITY_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "auth", label: "Auth" },
  { key: "admin", label: "Admin" },
  { key: "sessions", label: "Sessions" },
  { key: "content", label: "Content" },
];

function _activityBucket(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  const now = new Date();
  const day = (x) => Math.floor(x.getTime() / 86400000);
  const diff = day(now) - day(d);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return "This week";
  if (diff < 30) return "This month";
  return "Earlier";
}

function ActivityPane({ items, loading, category, setCategory, hasMore, onLoadMore }) {
  const grouped = useMemo(() => {
    const out = {};
    items.forEach((a) => {
      const bucket = _activityBucket(a.created_at);
      (out[bucket] = out[bucket] || []).push(a);
    });
    return out;
  }, [items]);
  const order = ["Today", "Yesterday", "This week", "This month", "Earlier"];

  return (
    <div className="space-y-4" data-testid="activity-pane">
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="activity-filters">
        {ACTIVITY_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            data-testid={`activity-cat-${c.key}`}
            className={cn(
              "px-2.5 py-1 rounded-full text-[11px] font-medium border transition",
              category === c.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400",
            )}
          >{c.label}</button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-8">Loading…</div>
      ) : !items.length ? (
        <div className="text-sm text-slate-400 text-center py-8">No activity recorded for this filter.</div>
      ) : (
        <div className="space-y-4" data-testid="activity-list">
          {order.filter((k) => grouped[k]).map((bucket) => (
            <div key={bucket}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{bucket}</div>
              <ul className="space-y-2">
                {grouped[bucket].map((a) => (
                  <li key={a.id} className="rounded-lg border border-slate-200 p-3" data-testid={`activity-${a.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono text-[11px] text-slate-500 truncate">{a.action}</span>
                        {a.category && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-px rounded bg-slate-100 text-slate-500 shrink-0">
                            {a.category}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    {(a.before || a.after) && (
                      <div className="mt-1 text-xs text-slate-600">
                        {a.before && <span className="text-rose-700">{JSON.stringify(a.before)}</span>}
                        {a.before && a.after && <span className="mx-1.5">→</span>}
                        {a.after && <span className="text-emerald-700">{JSON.stringify(a.after)}</span>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {hasMore && (
            <div className="pt-2 text-center">
              <Button size="sm" variant="outline" onClick={onLoadMore} disabled={loading} data-testid="activity-load-more">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SessionsPane({ items, loading, onRevoke, canEdit, isSelf }) {
  if (loading) return <div className="text-sm text-slate-400 text-center py-8">Loading sessions…</div>;
  if (!items.length) {
    return (
      <div className="text-sm text-slate-400 text-center py-8" data-testid="sessions-empty">
        No active sessions. The user is signed out everywhere.
      </div>
    );
  }
  // Best-effort detection of "this device" when the admin is viewing their
  // own drawer — match the most recent session whose user_agent equals the
  // current browser. Used to label and disable revoke for that row to avoid
  // accidental self-logout.
  const myUA = typeof navigator !== "undefined" ? (navigator.userAgent || "") : "";
  const currentSessionId = (() => {
    if (!isSelf || !myUA) return null;
    const match = items.find((s) => s.user_agent === myUA);
    return match?.id || null;
  })();
  return (
    <div className="space-y-2" data-testid="sessions-list">
      {items.map((s) => {
        const isCurrent = s.id === currentSessionId;
        return (
          <div key={s.id} className="rounded-lg border border-slate-200 p-3 flex items-start gap-3" data-testid={`session-${s.id}`}>
            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
              <Monitor className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-900">{s.device_label}</span>
                {isCurrent && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-px rounded-full bg-emerald-100 text-emerald-800 font-semibold" data-testid={`session-current-${s.id}`}>
                    This device
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 font-mono mt-0.5 break-all">{s.user_agent || "—"}</div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500 flex-wrap">
                {s.ip && <span>IP {s.ip}</span>}
                <span>Started {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</span>
                <span>Last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}</span>
              </div>
            </div>
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRevoke(s.id)}
                disabled={isCurrent}
                title={isCurrent ? "You can't revoke the device you're using" : ""}
                className="text-rose-700 border-rose-200 hover:bg-rose-50 shrink-0 disabled:opacity-50"
                data-testid={`session-revoke-${s.id}`}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Revoke
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ViewAsDialog({ data, onClose }) {
  const caps = data.effective_capabilities || [];
  const grouped = useMemo(() => {
    const out = {};
    caps.forEach((c) => { (out[c.module_label] = out[c.module_label] || []).push(c); });
    return out;
  }, [caps]);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="view-as-dialog"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/50" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
      >
        <header className="px-5 py-4 border-b border-slate-200 bg-amber-50/60 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-700" />
              <span className="font-display text-base font-semibold text-amber-900">Permission preview</span>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 font-semibold">read-only</span>
            </div>
            <div className="text-xs text-amber-800 mt-1">
              Viewing as <span className="font-mono">{data.email}</span> — this is a static permission preview, not impersonation.
            </div>
          </div>
          <button onClick={onClose} className="text-amber-800 hover:text-amber-900 p-1" data-testid="view-as-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="overflow-y-auto p-5">
          {caps.length === 0 ? (
            <div className="text-sm text-slate-400 italic text-center py-8">This user has no granted capabilities.</div>
          ) : (
            <div className="space-y-4">
              {Object.entries(grouped).map(([mod, list]) => (
                <div key={mod}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{mod}</div>
                  <ul className="space-y-1">
                    {list.map((c) => (
                      <li key={c.key} className="flex items-start gap-2 text-xs text-slate-700">
                        <LevelDot level={c.level} />
                        <div className="flex-1">
                          <span className="font-medium">{c.label}</span>
                          <span className={cn("ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium", LEVEL_TONE[c.level])}>{c.level}</span>
                          {c.via_role && <span className="ml-1.5 text-[10px] text-slate-500">via {c.via_role}</span>}
                          {c.rationale && <div className="text-slate-500 italic">{c.rationale}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ====================================================================== */
/* Permissions Tab                                                          */
/* ====================================================================== */
function PermissionsTab() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [highlightRole, setHighlightRole] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    api.get("/users/permissions/matrix")
      .then((r) => setMatrix(r.data))
      .catch(() => toast.error("Failed to load permission matrix"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !matrix) return <div className="text-sm text-slate-400 text-center py-12">Loading permission catalog…</div>;

  const filteredModules = matrix.modules.map((m) => {
    if (!search) return m;
    const q = search.toLowerCase();
    return {
      ...m,
      capabilities: m.capabilities.filter((c) =>
        c.label.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.key.toLowerCase().includes(q),
      ),
    };
  }).filter((m) => m.capabilities.length > 0);

  return (
    <section data-testid="permissions-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search capabilities…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 bg-slate-50 border-slate-200 w-[260px]"
              data-testid="permissions-search"
            />
          </div>
          <Select value={highlightRole || "all"} onValueChange={(v) => setHighlightRole(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="highlight-role-select">
              <SelectValue placeholder="Highlight a role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">No highlight</SelectItem>
              {matrix.roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setCompareOpen(true)} data-testid="open-compare-btn">
            <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Compare roles
          </Button>
        </div>
        <UserTypesLegend types={matrix.user_types} />
      </div>

      <div className="space-y-6">
        {filteredModules.map((m) => (
          <ModuleSection key={m.key} mod={m} roles={matrix.roles} highlightRole={highlightRole} />
        ))}
        {filteredModules.length === 0 && (
          <div className="text-sm text-slate-400 italic text-center py-12">No capabilities match "{search}".</div>
        )}
      </div>

      {compareOpen && <CompareRolesDialog roles={matrix.roles} onClose={() => setCompareOpen(false)} />}
    </section>
  );
}

function CompareRolesDialog({ roles, onClose }) {
  const initialPick = useMemo(() => {
    const keys = roles.map((r) => r.key);
    // Prefer manager + hrbp as a meaningful default; fall back to first two.
    const preferred = ["manager", "hrbp"].filter((k) => keys.includes(k));
    return preferred.length === 2 ? preferred : keys.slice(0, 2);
  }, [roles]);
  const [picked, setPicked] = useState(initialPick);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIdentical, setShowIdentical] = useState(false);

  const togglePick = (key) => {
    setPicked((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= 4) {
        toast.error("You can compare up to 4 roles");
        return prev;
      }
      return [...prev, key];
    });
  };

  const run = async () => {
    if (picked.length < 2) return toast.error("Pick at least 2 roles");
    setLoading(true);
    try {
      const r = await api.get("/admin/roles/compare", { params: { roles: picked.join(",") } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compare");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="compare-roles-dialog"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/50" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <header className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-base font-semibold text-slate-900">Compare roles side-by-side</div>
            <div className="text-xs text-slate-500 mt-1">Pick 2–4 roles to see exactly where their permissions diverge.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="compare-close-btn">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          {roles.map((r) => {
            const checked = picked.includes(r.key);
            return (
              <button
                key={r.key}
                onClick={() => togglePick(r.key)}
                data-testid={`compare-pick-${r.key}`}
                className={cn(
                  "px-2.5 py-1 rounded-full text-[11px] font-medium border transition",
                  checked ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400",
                )}
              >{r.label}</button>
            );
          })}
          <Button size="sm" className="ml-auto" onClick={run} disabled={loading} data-testid="compare-run-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Compare {picked.length || ""}
          </Button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          {!data ? (
            <div className="text-sm text-slate-400 italic text-center py-8">Pick roles and press Compare to see the diff.</div>
          ) : (
            <CompareResults data={data} showIdentical={showIdentical} setShowIdentical={setShowIdentical} />
          )}
        </div>
      </div>
    </div>
  );
}

function CompareResults({ data, showIdentical, setShowIdentical }) {
  const sectionRoles = data.roles;
  const renderRow = (row) => (
    <tr key={row.capability_key} className="border-b border-slate-50 last:border-0" data-testid={`compare-row-${row.capability_key}`}>
      <td className="px-4 py-2.5 align-top">
        <div className="text-xs font-medium text-slate-800">{row.capability_label}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{row.module_label}</div>
      </td>
      {sectionRoles.map((r) => {
        const lvl = row.levels[r.key] || "no";
        const note = row.rationale?.[r.key];
        return (
          <td key={r.key} className="text-center px-2 py-2.5 align-top" title={note || ""}>
            <LevelMark level={lvl} hasRationale={!!note} />
          </td>
        );
      })}
    </tr>
  );
  return (
    <div className="space-y-5" data-testid="compare-results">
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            Differences ({data.differences.length})
          </div>
        </div>
        {data.differences.length === 0 ? (
          <div className="text-xs text-slate-400 italic">These roles have identical permissions.</div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Capability</th>
                  {sectionRoles.map((r) => (
                    <th key={r.key} className="text-center px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[110px]">
                      {r.label.replace(" Admin", " Adm.").replace("HR Business Partner", "HRBP")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{data.differences.map(renderRow)}</tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <button
          onClick={() => setShowIdentical((v) => !v)}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center"
          data-testid="toggle-identical-btn"
        >
          {showIdentical ? "Hide" : "Show"} identical rows ({data.identical.length})
        </button>
        {showIdentical && data.identical.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-hidden mt-2">
            <table className="w-full text-sm">
              <tbody>{data.identical.map(renderRow)}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function UserTypesLegend({ types }) {
  return (
    <div className="flex items-center gap-1.5">
      {types.map((t) => (
        <span key={t.key} className={cn("inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border font-medium", USER_TYPE_TONE[t.key])}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

function ModuleSection({ mod, roles, highlightRole }) {
  return (
    <section className="axia-card overflow-hidden" data-testid={`module-${mod.key}`}>
      <header className="px-5 py-3.5 bg-slate-50/60 border-b border-slate-200">
        <div className="font-display text-base font-semibold text-slate-900">{mod.label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{mod.description}</div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-slate-100">
              <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Capability</th>
              {roles.map((r) => (
                <th
                  key={r.key}
                  className={cn(
                    "text-center px-2 py-2 text-[10px] uppercase tracking-wider font-semibold w-[90px]",
                    highlightRole === r.key ? "bg-amber-50 text-amber-900" : "text-slate-500",
                  )}
                >{r.label.replace(" Admin", " Adm.").replace("HR Business Partner", "HRBP")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mod.capabilities.map((c) => (
              <tr key={c.key} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40" data-testid={`cap-${c.key}`}>
                <td className="px-5 py-3 align-top">
                  <div className="font-medium text-slate-800">{c.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>
                </td>
                {roles.map((r) => {
                  const lvl = c.roles[r.key] || "no";
                  const rationale = c.rationale?.[r.key];
                  return (
                    <td
                      key={r.key}
                      title={rationale || ""}
                      className={cn(
                        "text-center px-2 py-3 align-top",
                        highlightRole === r.key ? "bg-amber-50/70" : "",
                      )}
                    >
                      <LevelMark level={lvl} hasRationale={!!rationale} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LevelMark({ level, hasRationale }) {
  if (level === "full") return <span className="text-emerald-600" title="Full access">✓</span>;
  if (level === "scoped") return <span className="inline-flex items-center text-blue-600 text-[10px] font-medium" title="Scoped access">scoped{hasRationale ? "*" : ""}</span>;
  if (level === "conditional") return <span className="inline-flex items-center text-amber-600 text-[10px] font-medium" title="Conditional access">cond.{hasRationale ? "*" : ""}</span>;
  return <span className="text-slate-300" title="No access">—</span>;
}
